import { useEffect, useState, useRef } from "react";
import { Bell, ClipboardList, FileText, Home, X } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

/**
 * Feb 2026 · Task 3 — Partner Portal notifications bell.
 *
 * Polls /api/partner/dashboard/notifications every 45s and renders:
 *   • a red badge with unread count over the bell icon
 *   • a popover listing the three buckets (leads, reports, bookings)
 *     with quick links to each destination page.
 *
 * Deliberately unobtrusive: no toasts, no sounds. Partners see it
 * next to the sign-out button on every partner-portal page.
 */
export default function PartnerNotifBell() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let timer;
    async function poll() {
      try {
        const r = await api.get("/partner/dashboard/notifications");
        if (!cancelled) setData(r.data);
      } catch {
        // silent — bell just goes empty
      } finally {
        if (!cancelled) timer = setTimeout(poll, 45000);
      }
    }
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, []);

  useEffect(() => {
    function onClick(e) {
      if (!open) return;
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const totals = data?.totals || {};
  const items  = data?.items  || {};
  const unread = totals.unread || 0;

  return (
    <div ref={wrapperRef} className="relative" data-testid="partner-notif-bell">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        className="relative inline-flex items-center justify-center w-9 h-9 rounded-full text-slate-600 hover:text-slate-900 hover:bg-slate-100"
        data-testid="partner-notif-bell-button"
      >
        <Bell className="w-4.5 h-4.5" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="partner-notif-bell-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          /* Sep 2026 — on a 390px screen the bell sits far enough right
             that a right-anchored 360px popover hung off the left edge
             and clipped its own content. Pin it inside the viewport on
             mobile, keep the anchored dropdown from `sm` up. */
          className="fixed inset-x-4 top-16 w-auto sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[360px] sm:max-w-[calc(100vw-2rem)] rounded-2xl bg-white border border-slate-200 shadow-xl overflow-hidden z-50"
          data-testid="partner-notif-popover"
        >
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-900">Notifications</div>
            <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700">
              <X className="w-4 h-4" />
            </button>
          </div>
          {unread === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500" data-testid="partner-notif-empty">
              You&#39;re all caught up.
            </div>
          ) : (
            <div className="max-h-[500px] overflow-y-auto">
              <NotifSection
                title="Customer quote requests"
                icon={ClipboardList}
                iconClass="text-amber-700 bg-amber-100"
                count={totals.new_quote_leads || 0}
                /* Sep 2026 bugfix — this used to point at
                   "/partner/quote-leads", which is the API path, not a
                   registered React route, so the click rendered a blank
                   page. The portal routes are /partner/leads and
                   /partner/leads/:leadId. */
                to="/partner/leads"
                items={(items.new_quote_leads || []).map((l) => ({
                  key: l.id,
                  primary: l.customer_name || l.customer_email || "New quote request",
                  secondary: l.property_address || l.package_name,
                  when: l.created_at,
                  href: `/partner/leads/${l.id}`,
                }))}
                closer={() => setOpen(false)}
              />
              <NotifSection
                title="Published reports (unopened)"
                icon={FileText}
                iconClass="text-cyan-700 bg-cyan-100"
                count={totals.new_reports || 0}
                to="/partner/bookings"
                items={(items.new_reports || []).map((r) => ({
                  key: r.id,
                  primary: r.booking_reference,
                  secondary: "Results email delivered — not opened yet",
                  when: r.partner_notify_sent_at,
                  href: `/partner/bookings/${r.booking_reference}`,
                }))}
                closer={() => setOpen(false)}
              />
              <NotifSection
                title="New bookings"
                icon={Home}
                iconClass="text-emerald-700 bg-emerald-100"
                count={totals.new_bookings || 0}
                to="/partner/bookings"
                items={(items.new_bookings || []).map((b) => ({
                  key: b.reference,
                  primary: `${(b.customer && b.customer.name) || "Customer"} · ${b.test_name || ""}`,
                  secondary: (b.customer && b.customer.suburb) || "",
                  when: b.created_at,
                  href: `/partner/bookings/${b.reference}`,
                }))}
                closer={() => setOpen(false)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NotifSection({ title, icon: Icon, iconClass, count, to, items, closer }) {
  if (!count) return null;
  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <div className="px-4 py-2 flex items-center gap-2 bg-slate-50">
        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${iconClass}`}>
          <Icon className="w-3.5 h-3.5" />
        </span>
        <div className="text-xs font-semibold text-slate-700">{title}</div>
        <span className="ml-auto text-[10px] font-bold uppercase tracking-wider rounded-full bg-slate-900 text-white px-2 py-0.5">
          {count}
        </span>
      </div>
      <ul className="divide-y divide-slate-100">
        {items.slice(0, 5).map((it) => (
          <li key={it.key} className="px-4 py-2.5 hover:bg-slate-50">
            <Link to={it.href || to} onClick={closer} className="block" data-testid={`partner-notif-item-${it.key}`}>
              <div className="text-sm font-medium text-slate-900 truncate">{it.primary}</div>
              {it.secondary && <div className="text-xs text-slate-500 truncate">{it.secondary}</div>}
              {it.when && (
                <div className="text-[11px] text-slate-400 mt-0.5">
                  {new Date(it.when).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                </div>
              )}
            </Link>
          </li>
        ))}
      </ul>
      {count > items.length && (
        <Link to={to} onClick={closer} className="block px-4 py-2 text-center text-xs font-semibold text-cyan-700 hover:text-cyan-900 hover:bg-slate-50">
          View all →
        </Link>
      )}
    </div>
  );
}
